import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Upload, File, Folder, FolderPlus, Download, Share2, Trash2, Eye,
  Search, Filter, Grid, List, RefreshCw, X, Check, FileText, Image,
  FileArchive, Video, Music, MoreVertical, Link as LinkIcon, Users
} from 'lucide-react';
import toast from 'react-hot-toast';
import * as documentsAPI from '../api/documents';
import { getProperties } from '../api/agent';
import { getContacts } from '../api/contact';
import { getLeads } from '../api/lead';

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docToDelete, setDocToDelete] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewTextContent, setPreviewTextContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);

  const [uploadData, setUploadData] = useState({
    name: '',
    description: '',
    category: 'miscellaneous',
    folder: '',
    related_property: '',
    related_contact: '',
    related_lead: ''
  });

  const [folderData, setFolderData] = useState({
    name: '',
    description: '',
    parent_folder: null
  });

  // Fetch static data once on mount (folders, categories, stats, relationships)
  useEffect(() => {
    fetchStaticData();
    fetchRelationshipData();
  }, []);

  // Fetch documents when filters change
  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedFolder, searchTerm]);

  const fetchStaticData = async () => {
    try {
      const [foldersData, categoriesData, statsData] = await Promise.all([
        documentsAPI.getFolders().catch(err => {
          console.error('Error fetching folders:', err);
          return [];
        }),
        documentsAPI.getDocumentCategories().catch(err => {
          console.error('Error fetching categories:', err);
          return [];
        }),
        documentsAPI.getDocumentStatistics().catch(err => {
          console.error('Error fetching statistics:', err);
          return { total_documents: 0, total_size_mb: 0 };
        })
      ]);

      let foldersList = Array.isArray(foldersData) ? foldersData : (foldersData?.results || foldersData?.data || []);
      let categoriesList = Array.isArray(categoriesData) ? categoriesData : (categoriesData?.results || categoriesData?.data || []);

      setFolders(foldersList);
      setCategories(categoriesList);
      setStats(statsData || { total_documents: 0, total_size_mb: 0 });
    } catch (err) {
      console.error('Error fetching static data:', err);
    }
  };

  const fetchDocuments = async (forceRefresh = false) => {
    setLoading(true);
    
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (selectedFolder) params.folder = selectedFolder;
      if (searchTerm) params.search = searchTerm;

      const docsData = await documentsAPI.getDocuments(params, !forceRefresh).catch(err => {
        console.error('Error fetching documents:', err);
        return [];
      });

      let docsList = Array.isArray(docsData) ? docsData : (docsData?.results || docsData?.data || []);
      setDocuments(docsList);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelationshipData = async () => {
    try {
      // Fetch properties, contacts, and leads for dropdowns using proper API functions
      const [propsResponse, contactsResponse, leadsResponse] = await Promise.all([
        getProperties().catch(err => {
          console.error('Error fetching properties:', err);
          return { data: { results: [] } };
        }),
        getContacts().catch(err => {
          console.error('Error fetching contacts:', err);
          return { data: { results: [] } };
        }),
        getLeads().catch(err => {
          console.error('Error fetching leads:', err);
          return { data: { results: [] } };
        })
      ]);

      // Extract data from axios responses
      const propertiesData = propsResponse.data?.results || propsResponse.data || [];
      const contactsData = contactsResponse.data?.results || contactsResponse.data || [];
      const leadsData = leadsResponse.data?.results || leadsResponse.data || [];

      console.log('Fetched relationship data:', {
        properties: propertiesData.length,
        contacts: contactsData.length,
        leads: leadsData.length
      });

      setProperties(propertiesData);
      setContacts(contactsData);
      setLeads(leadsData);
    } catch (err) {
      console.error('Error fetching relationship data:', err);
      // Set empty arrays on error
      setProperties([]);
      setContacts([]);
      setLeads([]);
    }
  };


  const handleRefresh = () => {
    console.log('Manual refresh triggered');
    documentsAPI.invalidateDocumentCache();
    fetchDocuments(true); // Force refresh, bypass cache
    fetchStaticData(); // Also refresh static data
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setUploadData({ ...uploadData, file: files[0], name: files[0].name });
      setShowUploadModal(true);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setUploadData({ ...uploadData, file, name: file.name });
      setShowUploadModal(true);
    }
  }, [uploadData]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('name', uploadData.name);
      formData.append('description', uploadData.description);
      formData.append('category', uploadData.category);
      if (uploadData.folder) formData.append('folder', uploadData.folder);
      if (uploadData.related_property) formData.append('related_property', uploadData.related_property);
      if (uploadData.related_contact) formData.append('related_contact', uploadData.related_contact);
      if (uploadData.related_lead) formData.append('related_lead', uploadData.related_lead);

      const uploadedDoc = await documentsAPI.uploadDocument(formData);
      console.log('Document uploaded:', uploadedDoc);
      
      // Show success message
      toast.success('Document uploaded successfully!');
      
      setShowUploadModal(false);
      resetUploadForm();
      
      // Invalidate all document caches and force refresh
      documentsAPI.invalidateDocumentCache(); // This invalidates all document caches
      await fetchDocuments(true); // Force refresh documents
      await fetchStaticData(); // Refresh static data
    } catch (err) {
      console.error('Error uploading document:', err);
      toast.error(err.response?.data?.message || 'Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    try {
      const newFolder = await documentsAPI.createFolder(folderData);
      console.log('Folder created:', newFolder);
      setShowFolderModal(false);
      setFolderData({ name: '', description: '', parent_folder: null });
      
      // Invalidate all document caches and force refresh
      documentsAPI.invalidateDocumentCache(); // This invalidates all document caches
      await fetchDocuments(true); // Force refresh documents
      await fetchStaticData(); // Refresh static data
      
      toast.success('Folder created successfully!');
    } catch (err) {
      console.error('Error creating folder:', err);
      toast.error('Failed to create folder: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDownload = async (doc) => {
    try {
      const blob = await documentsAPI.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
      toast.error('Failed to download document');
    }
  };

  const handleDelete = (doc) => {
    setDocToDelete(doc);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;

    try {
      await documentsAPI.deleteDocument(docToDelete.id);
      console.log('Document deleted:', docToDelete.id);
      
      setShowDeleteModal(false);
      setDocToDelete(null);
      
      // Invalidate all document caches and force refresh
      documentsAPI.invalidateDocumentCache(); // This invalidates all document caches
      await fetchDocuments(true); // Force refresh documents
      await fetchStaticData(); // Refresh static data
      
      toast.success('Document deleted successfully!');
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error('Failed to delete document');
      setShowDeleteModal(false);
      setDocToDelete(null);
    }
  };

  const handleShare = (doc) => {
    setSelectedDoc(doc);
    setShowShareModal(true);
  };

  const togglePublic = async (doc) => {
    try {
      const response = await documentsAPI.shareDocument(doc.id, { is_public: !doc.is_public });
      console.log('Document sharing toggled:', doc.id, response);
      
      // Only invalidate cache for this specific document and the list
      documentsAPI.invalidateDocumentCache(`detail_${doc.id}`);
      documentsAPI.invalidateDocumentCache('list');
      
      // Update the document in the local state without full refetch
      setDocuments(prevDocs => 
        prevDocs.map(d => 
          d.id === doc.id 
            ? { ...d, is_public: !doc.is_public, share_link: response.share_link } 
            : d
        )
      );
      
      setShowShareModal(false);
      setSelectedDoc(null);
      
      toast.success('Sharing settings updated successfully!');
    } catch (err) {
      console.error('Error toggling public status:', err);
      toast.error('Failed to update sharing settings');
    }
  };

  const resetUploadForm = () => {
    setUploadData({
      name: '',
      description: '',
      category: 'miscellaneous',
      folder: '',
      related_property: '',
      related_contact: '',
      related_lead: ''
    });
  };

  const getFileIcon = (doc) => {
    const ext = doc.file_extension?.toLowerCase();
    if (['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.ico'].includes(ext)) {
      return <Image className="w-5 h-5 text-blue-500" />;
    } else if (['.pdf'].includes(ext)) {
      return <FileText className="w-5 h-5 text-red-500" />;
    } else if (['.doc', '.docx', '.txt', '.rtf', '.odt'].includes(ext)) {
      return <FileText className="w-5 h-5 text-blue-600" />;
    } else if (['.xls', '.xlsx', '.csv', '.ods'].includes(ext)) {
      return <FileText className="w-5 h-5 text-green-600" />;
    } else if (['.ppt', '.pptx', '.odp'].includes(ext)) {
      return <FileText className="w-5 h-5 text-orange-600" />;
    } else if (['.zip', '.rar', '.7z', '.tar', '.gz'].includes(ext)) {
      return <FileArchive className="w-5 h-5 text-yellow-600" />;
    } else if (['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm'].includes(ext)) {
      return <Video className="w-5 h-5 text-purple-600" />;
    } else if (['.mp3', '.wav', '.flac', '.aac', '.ogg'].includes(ext)) {
      return <Music className="w-5 h-5 text-pink-600" />;
    }
    return <File className="w-5 h-5 text-gray-600" />;
  };

  const canPreview = (doc) => {
    const ext = doc.file_extension?.toLowerCase();
    // Can preview images, PDFs, text files
    const previewable = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.pdf', '.txt'];
    return previewable.includes(ext);
  };

  const handlePreview = async (doc) => {
    setSelectedDoc(doc);
    setShowPreviewModal(true);
    setPreviewLoading(true);
    setPreviewBlobUrl(null);
    setPreviewTextContent(null);

    try {
      if (doc.file_extension?.toLowerCase() === '.pdf') {
        // For PDFs, download as blob and create blob URL
        const blob = await documentsAPI.downloadDocument(doc.id);
        const blobUrl = URL.createObjectURL(blob);
        setPreviewBlobUrl(blobUrl);
      } else if (doc.file_extension?.toLowerCase() === '.txt') {
        // For text files, fetch as text
        const blob = await documentsAPI.downloadDocument(doc.id);
        const text = await blob.text();
        setPreviewTextContent(text);
      }
      // For images, we'll use the file_url directly (img tag doesn't need blob)
    } catch (err) {
      console.error('Error loading preview:', err);
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Cleanup blob URL when modal closes or component unmounts
  useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  // Cleanup when modal closes
  useEffect(() => {
    if (!showPreviewModal && previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
      setPreviewTextContent(null);
    }
  }, [showPreviewModal, previewBlobUrl]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3 sm:gap-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Documents</h1>
            <p className="text-sm sm:text-base text-gray-600">Manage and organize your files</p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => setShowFolderModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <FolderPlus className="w-4 h-4" />
              New Folder
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
              style={{ backgroundColor: '#55C5D0' }}
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Total Documents</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-800">{stats.total_documents}</p>
                </div>
                <File className="w-8 h-8" style={{ color: '#55C5D0' }} />
              </div>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Total Size</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-800">{stats.total_size_mb?.toFixed(2)} MB</p>
                </div>
                <FileArchive className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Folders</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-800">{folders.length}</p>
                </div>
                <Folder className="w-8 h-8 text-yellow-500" />
              </div>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Categories</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-800">{categories.length}</p>
                </div>
                <Grid className="w-8 h-8 text-purple-500" />
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#55C5D0' }}
              />
            </div>
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2"
            style={{ '--tw-ring-color': '#55C5D0' }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'text-white' : 'bg-gray-100'}`}
              style={viewMode === 'grid' ? { backgroundColor: '#55C5D0' } : {}}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'text-white' : 'bg-gray-100'}`}
              style={viewMode === 'list' ? { backgroundColor: '#55C5D0' } : {}}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Drag and Drop Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`mb-6 border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive ? 'bg-opacity-10' : 'border-gray-300 bg-gray-50'
        }`}
        style={dragActive ? { borderColor: '#55C5D0', backgroundColor: '#55C5D01A' } : {}}
      >
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 mb-2">Drag and drop files here</p>
        <p className="text-sm text-gray-500">or click the Upload button above</p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#55C5D0' }}></div>
          <p className="text-gray-600 mt-4">Loading documents...</p>
        </div>
      )}

      {/* Folders Section */}
      {!loading && folders.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Folder className="w-5 h-5" style={{ color: '#55C5D0' }} />
            Folders ({folders.length})
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
            {folders.map(folder => (
              <button
                key={folder.id}
                onClick={() => setSelectedFolder(folder.full_path || folder.name)}
                className="flex flex-col items-center justify-center p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md hover:border-teal-200 transition-all"
              >
                <Folder className="w-12 h-12 mb-2 text-yellow-500" />
                <span className="text-sm font-medium text-gray-700 text-center truncate w-full">
                  {folder.name}
                </span>
                {folder.document_count > 0 && (
                  <span className="text-xs text-gray-500 mt-1">
                    {folder.document_count} files
                  </span>
                )}
              </button>
            ))}
          </div>
          {selectedFolder && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-gray-600">Current folder:</span>
              <span className="text-sm font-medium text-teal-600">{selectedFolder}</span>
              <button
                onClick={() => setSelectedFolder('')}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {/* Documents Grid/List */}
      {!loading && (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {documents.map(doc => (
                <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getFileIcon(doc)}
                      <span className="text-xs px-2 py-1 bg-gray-100 rounded">{doc.file_extension}</span>
                    </div>
                    <div className="flex gap-1">
                      {canPreview(doc) && (
                        <button
                          onClick={() => handlePreview(doc)}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(doc)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Download"
                      >
                        <Download className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleShare(doc)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Share"
                      >
                        <Share2 className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-medium text-gray-800 mb-1 truncate" title={doc.name}>
                    {doc.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">{doc.category}</p>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>{formatFileSize(doc.file_size || 0)}</span>
                    <span>{formatDate(doc.uploaded_at)}</span>
                  </div>
                  {doc.is_public && (
                    <div className="mt-2 flex items-center gap-1 text-xs" style={{ color: '#55C5D0' }}>
                      <Users className="w-3 h-3" />
                      Public
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uploaded</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {documents.map(doc => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {getFileIcon(doc)}
                          <span className="font-medium text-gray-800">{doc.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{doc.category}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{formatFileSize(doc.file_size || 0)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{formatDate(doc.uploaded_at)}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {canPreview(doc) && (
                            <button
                              onClick={() => handlePreview(doc)}
                              className="p-1 hover:bg-gray-100 rounded"
                              title="Preview"
                            >
                              <Eye className="w-4 h-4 text-blue-600" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDownload(doc)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Download"
                          >
                            <Download className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleShare(doc)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Share"
                          >
                            <Share2 className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(doc)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {documents.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No documents found</p>
              <p className="text-sm text-gray-500">Upload your first document to get started</p>
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Upload Document</h2>
              <button onClick={() => {setShowUploadModal(false); resetUploadForm();}} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleUpload}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                  <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                    {uploadData.file?.name || 'No file selected'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={uploadData.name}
                    onChange={(e) => setUploadData({...uploadData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                    style={{ '--tw-ring-color': '#55C5D0' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                    style={{ '--tw-ring-color': '#55C5D0' }}
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={uploadData.category}
                    onChange={(e) => setUploadData({...uploadData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                    style={{ '--tw-ring-color': '#55C5D0' }}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* Property Association */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link to Property (Optional)</label>
                  <select
                    value={uploadData.related_property}
                    onChange={(e) => setUploadData({...uploadData, related_property: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                    style={{ '--tw-ring-color': '#55C5D0' }}
                  >
                    <option value="">None</option>
                    {properties.map(prop => (
                      <option key={prop.id} value={prop.id}>
                        {prop.address || prop.title || `Property #${prop.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Contact Association */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link to Contact (Optional)</label>
                  <select
                    value={uploadData.related_contact}
                    onChange={(e) => setUploadData({...uploadData, related_contact: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                    style={{ '--tw-ring-color': '#55C5D0' }}
                  >
                    <option value="">None</option>
                    {contacts.map(contact => (
                      <option key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name} - {contact.email}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Lead Association */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link to Lead (Optional)</label>
                  <select
                    value={uploadData.related_lead}
                    onChange={(e) => setUploadData({...uploadData, related_lead: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                    style={{ '--tw-ring-color': '#55C5D0' }}
                  >
                    <option value="">None</option>
                    {leads.map(lead => (
                      <option key={lead.id} value={lead.id}>
                        {lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed Lead'} - {lead.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {setShowUploadModal(false); resetUploadForm();}}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#55C5D0' }}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Folder Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Create Folder</h2>
              <button onClick={() => setShowFolderModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleCreateFolder}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Folder Name *</label>
                  <input
                    type="text"
                    value={folderData.name}
                    onChange={(e) => setFolderData({...folderData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                    style={{ '--tw-ring-color': '#55C5D0' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={folderData.description}
                    onChange={(e) => setFolderData({...folderData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                    style={{ '--tw-ring-color': '#55C5D0' }}
                    rows="3"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowFolderModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: '#55C5D0' }}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Share Document</h2>
              <button onClick={() => {setShowShareModal(false); setSelectedDoc(null);}} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Document: {selectedDoc.name}</p>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-600" />
                  <span className="text-sm">Make Public</span>
                </div>
                <button
                  onClick={() => togglePublic(selectedDoc)}
                  className="relative inline-flex h-6 w-11 items-center rounded-full"
                  style={{ backgroundColor: selectedDoc.is_public ? '#55C5D0' : '#D1D5DB' }}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      selectedDoc.is_public ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {selectedDoc.is_public && selectedDoc.share_link && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Share Link</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/document-management/shared/${selectedDoc.share_link}/`}
                      readOnly
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      onClick={() => {
                        const shareUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/document-management/shared/${selectedDoc.share_link}/`;
                        navigator.clipboard.writeText(shareUrl);
                        toast.success('Link copied to clipboard!');
                      }}
                      className="px-3 py-2 text-white rounded-lg hover:opacity-90"
                      style={{ backgroundColor: '#55C5D0' }}
                    >
                      <LinkIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Anyone with this link can download the document (no login required)
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={() => {setShowShareModal(false); setSelectedDoc(null);}}
              className="w-full mt-6 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-6xl mx-4 h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-800">{selectedDoc.name}</h2>
                <p className="text-sm text-gray-600">
                  {selectedDoc.category} • {formatFileSize(selectedDoc.file_size || 0)} • {selectedDoc.file_extension}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDownload(selectedDoc)}
                  className="px-3 py-2 text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: '#55C5D0' }}
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => {
                    setShowPreviewModal(false);
                    setSelectedDoc(null);
                    // Cleanup blob URL
                    if (previewBlobUrl) {
                      URL.revokeObjectURL(previewBlobUrl);
                      setPreviewBlobUrl(null);
                    }
                    setPreviewTextContent(null);
                  }} 
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-hidden p-4">
              {previewLoading && (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#55C5D0' }}></div>
                    <p className="text-gray-600">Loading preview...</p>
                  </div>
                </div>
              )}
              
              {!previewLoading && selectedDoc.file_extension?.toLowerCase() === '.pdf' && previewBlobUrl && (
                <object
                  data={previewBlobUrl}
                  type="application/pdf"
                  className="w-full h-full border rounded"
                  title="PDF Preview"
                >
                  <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded">
                    <div className="text-center">
                      <p className="text-gray-600 mb-4">Unable to display PDF preview</p>
                      <button
                        onClick={() => handleDownload(selectedDoc)}
                        className="px-4 py-2 text-white rounded-lg hover:opacity-90"
                        style={{ backgroundColor: '#55C5D0' }}
                      >
                        Download PDF
                      </button>
                    </div>
                  </div>
                </object>
              )}
              
              {!previewLoading && ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].includes(selectedDoc.file_extension?.toLowerCase()) && (
                <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded">
                  <img
                    src={selectedDoc.file_url}
                    alt={selectedDoc.name}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      // Fallback: try downloading as blob if direct URL fails
                      documentsAPI.downloadDocument(selectedDoc.id)
                        .then(blob => {
                          const blobUrl = URL.createObjectURL(blob);
                          e.target.src = blobUrl;
                        })
                        .catch(err => console.error('Error loading image:', err));
                    }}
                  />
                </div>
              )}
              
              {!previewLoading && selectedDoc.file_extension?.toLowerCase() === '.txt' && previewTextContent !== null && (
                <div className="w-full h-full overflow-auto bg-gray-50 p-4 rounded font-mono text-sm">
                  <pre className="whitespace-pre-wrap text-gray-800">{previewTextContent}</pre>
                </div>
              )}
            </div>
            
            {/* Document Info */}
            <div className="p-4 border-t bg-gray-50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Uploaded by:</span>
                  <p className="text-gray-600">{selectedDoc.uploaded_by_name}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Uploaded:</span>
                  <p className="text-gray-600">{formatDate(selectedDoc.uploaded_at)}</p>
                </div>
                {selectedDoc.property_address && (
                  <div>
                    <span className="font-medium text-gray-700">Property:</span>
                    <p className="text-gray-600">{selectedDoc.property_address}</p>
                  </div>
                )}
                {selectedDoc.contact_name && (
                  <div>
                    <span className="font-medium text-gray-700">Contact:</span>
                    <p className="text-gray-600">{selectedDoc.contact_name}</p>
                  </div>
                )}
                {selectedDoc.lead_name && (
                  <div>
                    <span className="font-medium text-gray-700">Lead:</span>
                    <p className="text-gray-600">{selectedDoc.lead_name}</p>
                  </div>
                )}
              </div>
              {selectedDoc.description && (
                <div className="mt-3">
                  <span className="font-medium text-gray-700">Description:</span>
                  <p className="text-gray-600 mt-1">{selectedDoc.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && docToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Are you sure you want to delete this document?
            </h3>
            <p className="text-gray-600 mb-6">
              <span className="font-medium">{docToDelete.name}</span> will be permanently deleted. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDocToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;

